/**
 * Where the service worker files a reference-data response.
 *
 * ## Why the URL alone is the wrong key
 *
 * `/v1/accounts` and `/v1/companies/current` are the same URL for every company
 * and every person; which books they describe is decided by the bearer token.
 * The Cache API ignores the Authorization header, and the API varies only on
 * Origin, so a response cached by URL is served back to *any* request for that
 * URL. Offline, that meant a person who had switched from one company to another
 * saw the first company's chart of accounts and company record under the second
 * company's name, and on a shared device the next person saw the previous
 * person's company.
 *
 * So the key carries the company and the user the token names, and a request
 * with no readable token is not cached at all.
 *
 * ## Why the token is decoded and not verified
 *
 * The key only *partitions* the cache. It grants nothing: every answer that
 * reaches the cache came from the API, which verified the token that fetched
 * it. A forged token could at most select an entry already stored in this same
 * browser's Cache Storage, which anyone with that access can read directly.
 *
 * Shared by the page and the service worker, so this file uses nothing but
 * `URL`, `atob` and `JSON`.
 */

/**
 * The cache the worker writes to.
 *
 * `v2` because the `v1` entries were keyed by URL alone; the worker deletes
 * `v1` on activation so none of them can ever be served again.
 */
export const REFERENCE_CACHE = 'accountyob-reference-v2';

/** The cache name that must never be read again. */
export const UNSCOPED_REFERENCE_CACHE = 'accountyob-reference-v1';

export interface TokenScope {
  readonly companyId: string;
  readonly userId: string;
}

/** The company and user a `Bearer` company token names, or null. */
export function tokenScope(authorization: string | null): TokenScope | null {
  const match = /^Bearer\s+([^.\s]+)\.([^.\s]+)\.([^.\s]+)$/u.exec(authorization ?? '');
  const payloadPart = match?.[2];
  if (payloadPart === undefined) {
    return null;
  }
  try {
    const base64 = payloadPart.replace(/-/gu, '+').replace(/_/gu, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { companyId?: unknown; userId?: unknown };
    if (typeof claims.companyId !== 'string' || typeof claims.userId !== 'string') {
      return null;
    }
    if (claims.companyId === '' || claims.userId === '') {
      return null;
    }
    return { companyId: claims.companyId, userId: claims.userId };
  } catch {
    return null;
  }
}

/**
 * The key a reference response is stored and looked up under, or null when the
 * request must not be cached.
 *
 * Query parameters rather than a fragment: the Cache API ignores fragments when
 * matching, so a fragment would collapse every company back onto one entry.
 */
export function referenceCacheKey(url: string, authorization: string | null): string | null {
  const scope = tokenScope(authorization);
  if (scope === null) {
    return null;
  }
  const keyed = new URL(url);
  keyed.searchParams.set('__cache_company', scope.companyId);
  keyed.searchParams.set('__cache_user', scope.userId);
  return keyed.toString();
}
